"use client"

import { useRef } from "react"
import { useSetAtom } from "jotai"
import { LuLoaderCircle as LuLoader } from "react-icons/lu"
import { useQueryClient, type QueryKey } from "@tanstack/react-query"
import { cn } from "~/lib/utils"
import { usePullToRefresh } from "~/hooks/usePullToRefresh"
import { errorBoundaryResetVersionAtom } from "~/atoms/error-boundary-reset"
import { relativeTimeNowAtom } from "~/atoms/relative-time-now"

interface Props {
  className?: string
  children: React.ReactNode
  queryKeyPrefix: QueryKey
  useWindowScroll?: boolean
}

export function PullToRefreshWrapper({ className, children, queryKeyPrefix, useWindowScroll = false }: Props) {
  const queryClient = useQueryClient()
  const bumpErrorBoundaryReset = useSetAtom(errorBoundaryResetVersionAtom)
  const setRelativeTimeNow = useSetAtom(relativeTimeNowAtom)
  const containerRef = useRef<HTMLDivElement>(null)
  const { pullDistance, isRefreshing } = usePullToRefresh(containerRef, () => {
    bumpErrorBoundaryReset((version) => version + 1)
    queryClient.invalidateQueries({ queryKey: queryKeyPrefix })
    setRelativeTimeNow(Date.now())
  }, useWindowScroll)

  const indicator = (
    <div
      className={cn(
        "ptr-indicator left-1/2 z-10 flex size-8 items-center justify-center rounded-full bg-card shadow-md",
        useWindowScroll ? "fixed top-[calc(var(--header-size)+0.5rem)]" : "absolute top-2",
      )}
      style={{
        "--ptr-y": `${isRefreshing ? 8 : pullDistance - 32}px`,
        "--ptr-opacity": String(isRefreshing ? 1 : pullDistance / 32),
      } as React.CSSProperties}
    >
      <LuLoader className="animate-spin size-6 text-secondary" strokeWidth={2.0} />
    </div>
  )

  if (useWindowScroll) {
    return (
      <div className={className}>
        {indicator}
        {children}
      </div>
    )
  }

  return (
    <div className={cn("relative overflow-clip", className)}>
      {indicator}
      <div
        ref={containerRef}
        data-scroll-restoration-id={queryKeyPrefix.join(":")}
        className="h-full min-h-0 overflow-y-auto overscroll-y-auto overscroll-ios-auto"
      >
        {children}
      </div>
    </div>
  )
}
